import '@massilia/ui/styles.css'

import '@/index.css'
import '@/styles.css'
import '@/widgets/research-bar-widget-overrides.css'
import { PortalContainerProvider } from '@massilia/ui'
import { QueryClientProvider } from '@tanstack/react-query'
import { defaultStringifySearch } from '@tanstack/react-router'
import React from 'react'
import ReactDOM from 'react-dom/client'

import { type ChatWidgetHost, ChatWidgetPanel } from '@/components/chat/chat-widget'
import { ResearchBarFormShell } from '@/components/research-bar/research-bar-form-shell'
import { isChatWidgetEnabled } from '@/lib/feature-flags'
import { createAppQueryClient } from '@/lib/query-client'
import { buildConversationHandoffHash } from '@/stores/chat-widget.store'

const WIDGET_SELECTOR = '[data-massilia-research-bar]'
const WIDGET_SCRIPT_NAME = 'research-bar-widget.js'
const WIDGET_STYLE_NAME = 'research-bar-widget.css'
const SHADOW_STYLE_DATA_ATTR = 'data-massilia-research-bar-widget-style'
const WIDGET_BASE_ORIGIN_KEY = '__MASSILIA_WIDGET_BASE_ORIGIN__'

const mountedRoots = new WeakMap<Element, ReactDOM.Root>()
const widgetScriptSrc = resolveWidgetScriptSrc()
const widgetBaseOrigin = resolveWidgetBaseOrigin()
// Son propre client : le widget est monté dans le site d'un tiers, où rien ne
// garantit qu'une seule instance existe sur la page.
const queryClient = createAppQueryClient()

function resolveWidgetBaseOrigin() {
  if (!widgetScriptSrc) {
    return null
  }

  return new URL(widgetScriptSrc).origin
}

function resolveWidgetCssHref() {
  if (!widgetScriptSrc) {
    return null
  }

  const cssUrl = new URL(widgetScriptSrc)
  cssUrl.pathname = cssUrl.pathname.replace(WIDGET_SCRIPT_NAME, WIDGET_STYLE_NAME)

  return cssUrl.toString()
}

function resolveWidgetScriptSrc() {
  const currentScript = document.currentScript as HTMLScriptElement | null
  if (currentScript?.src) {
    return currentScript.src
  }

  const scripts = Array.from(document.querySelectorAll('script[src]')).reverse()
  const widgetScript = scripts.find((script) => {
    const src = script.getAttribute('src')
    return src?.includes(WIDGET_SCRIPT_NAME)
  }) as HTMLScriptElement | undefined

  return widgetScript?.src ?? null
}

declare global {
  interface Window {
    __MASSILIA_WIDGET_BASE_ORIGIN__?: string
    MassiliaResearchBar?: {
      mount: (selector?: string) => void
      mountElement: (element: Element) => void
    }
  }
}

function ensureShadowStyles(shadowRoot: ShadowRoot) {
  if (shadowRoot.querySelector(`[${SHADOW_STYLE_DATA_ATTR}]`)) {
    return
  }

  const widgetCssHref = resolveWidgetCssHref()
  if (!widgetCssHref) {
    return
  }

  const hostStyleReset = document.createElement('style')
  hostStyleReset.textContent = ':host{all:initial;display:block;}'
  shadowRoot.appendChild(hostStyleReset)

  const stylesheet = document.createElement('link')
  stylesheet.rel = 'stylesheet'
  stylesheet.href = widgetCssHref
  stylesheet.setAttribute(SHADOW_STYLE_DATA_ATTR, 'true')
  shadowRoot.appendChild(stylesheet)
}

function getShadowRoot(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return null
  }

  if (element.shadowRoot) {
    return element.shadowRoot
  }

  return element.attachShadow({ mode: 'open' })
}

function mount(selector = WIDGET_SELECTOR) {
  const placeholders = document.querySelectorAll(selector)
  for (const placeholder of placeholders) {
    mountElement(placeholder)
  }
}

/**
 * Le chat monte avec la barre de recherche : ce script est déjà chargé sur
 * toutes les pages du WordPress, le chat y apparaît donc sans rien toucher côté WP.
 * Hors de l'app, pas de routeur : ouvrir les résultats quitte la page vers l'app.
 */
function mountChat() {
  if (!isChatWidgetEnabled || !widgetBaseOrigin || document.querySelector('[data-massilia-chat]')) {
    return
  }

  const element = document.createElement('div')
  element.setAttribute('data-massilia-chat', '')
  // Au-dessus des bandeaux et en-têtes collants du thème, comme les overlays de la barre.
  element.style.cssText = 'position:fixed;z-index:2147483000;width:0;height:0;'
  document.body.append(element)

  const shadowRoot = element.attachShadow({ mode: 'open' })
  ensureShadowStyles(shadowRoot)
  const appContainer = document.createElement('div')
  appContainer.className = 'massilia-research-bar-widget-scope'
  shadowRoot.append(appContainer)

  const origin = widgetBaseOrigin
  const host: ChatWidgetHost = {
    pathname: window.location.pathname,
    assetOrigin: origin,
    openFlightResults: (search) => {
      // Différé : le widget consomme l'action et ferme le chat juste après cet
      // appel, et c'est cet état-là, persisté, qui doit partir vers l'app.
      setTimeout(() => {
        const path = `/vols-multi/resultats${defaultStringifySearch(search)}`
        window.location.assign(new URL(path + buildConversationHandoffHash(), origin))
      })
    },
    search: {},
  }

  ReactDOM.createRoot(appContainer).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ChatWidgetPanel host={host} />
      </QueryClientProvider>
    </React.StrictMode>
  )
}

function mountElement(element: Element) {
  if (mountedRoots.has(element)) {
    return
  }

  const shadowRoot = getShadowRoot(element)
  if (!shadowRoot) {
    return
  }

  ensureShadowStyles(shadowRoot)

  const appContainer = document.createElement('div')
  const portalContainer = document.createElement('div')
  appContainer.className = 'massilia-research-bar-widget-scope'
  portalContainer.className = 'massilia-research-bar-widget-scope'
  portalContainer.style.height = '0'
  portalContainer.style.inset = '0'
  portalContainer.style.position = 'fixed'
  portalContainer.style.width = '100%'
  portalContainer.style.zIndex = '2147483000'
  shadowRoot.append(appContainer, portalContainer)

  const root = ReactDOM.createRoot(appContainer)
  mountedRoots.set(element, root)

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <PortalContainerProvider container={portalContainer}>
          <div className="massilia-research-bar-widget-root massilia-research-bar-widget flex w-full items-stretch justify-center p-2 md:p-4">
            <ResearchBarFormShell
              className="w-full items-stretch p-3 shadow-none md:p-4"
              errorPopoverSide="bottom"
              errorsDisplayType="popover"
              useWordpressDialog
            />
          </div>
        </PortalContainerProvider>
      </QueryClientProvider>
    </React.StrictMode>
  )
}

window.MassiliaResearchBar = {
  mount,
  mountElement,
}

function start() {
  if (widgetBaseOrigin) {
    window[WIDGET_BASE_ORIGIN_KEY] = widgetBaseOrigin
  }

  mount()
  mountChat()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start)
} else {
  start()
}
